/**
 * FontAwesome Icon Library Initialization (Synchronous Version)
 *
 * This file provides a synchronous way to import and register FontAwesome icons
 * for immediate use in components like App.jsx. It's a fallback for cases where
 * async imports aren't supported.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import { library } from '@fortawesome/fontawesome-svg-core'
import { ICON_STYLES } from './index'
import { resolveIconDefinition } from './iconAliases'

// Import all icon packages synchronously
import * as brandIcons from '@fortawesome/free-brands-svg-icons'
import * as regularIcons from '@fortawesome/free-regular-svg-icons'
import * as solidIcons from '@fortawesome/free-solid-svg-icons'

/**
 * Icon package mapping for synchronous imports
 */
const ICON_PACKAGES = {
  brands: brandIcons,
  // Map unsupported styles to nearest free equivalents
  duotone: solidIcons, // alias 'fad' to 'fas'
  light: regularIcons, // alias 'fal' to 'far'
  regular: regularIcons,
  solid: solidIcons,
}

/**
 * Synchronously imports and registers FontAwesome icons
 * @param {string} style - The icon style (brands, duotone, light, regular, solid)
 * @param {Array} iconNames - Array of icon names to import
 * @returns {Array} Array of imported icon objects
 */
const importIconsSync = (style, iconNames) => {
  try {
    const iconPackage = ICON_PACKAGES[style]
    if (!iconPackage) {
      throw new Error(`Unknown icon style: ${style}`)
    }

    return iconNames
      .map((iconName) => {
        const fullIconName = iconName.startsWith('fa')
          ? iconName
          : `fa${iconName}`

        const icon = resolveIconDefinition({
          fullIconName,
          style,
          primaryPackage: iconPackage,
          solidPackage: solidIcons,
          regularPackage: regularIcons,
        })

        if (!icon) {
          console.warn(
            `Icon not found: ${fullIconName} in ${style} package`,
          )
          return null
        }

        return icon
      })
      .filter(Boolean)
  } catch (error) {
    console.error(`Error importing ${style} icons:`, error)
    return []
  }
}

/**
 * Initializes the FontAwesome icon library with all configured icons (synchronous)
 * @returns {void}
 */
export const initializeIconLibrarySync = () => {
  try {
    // Import and register icons for each style
    for (const [style, iconNames] of Object.entries(ICON_STYLES)) {
      if (iconNames.length > 0) {
        const icons = importIconsSync(style, iconNames)
        if (icons.length > 0) {
          library.add(...icons)
        }
      }
    }
  } catch (error) {
    console.error(
      'Failed to initialize FontAwesome icon library (sync):',
      error,
    )
  }
}

/**
 * Registers a specific set of icons by style (synchronous)
 * @param {string} style - The icon style to register
 * @returns {void}
 */
export const registerIconStyleSync = (style) => {
  if (!ICON_STYLES[style]) {
    throw new Error(`Unknown icon style: ${style}`)
  }

  const icons = importIconsSync(style, ICON_STYLES[style])
  if (icons.length > 0) {
    library.add(...icons)
  }
}

/**
 * Registers a specific icon by name and style (synchronous)
 * @param {string} iconName - The name of the icon to register
 * @param {string} style - The style of the icon
 * @returns {boolean} Success status
 */
export const registerSingleIconSync = (iconName, style) => {
  try {
    const icons = importIconsSync(style, [iconName])
    if (icons.length > 0) {
      library.add(...icons)
      return true
    }
    return false
  } catch (error) {
    console.error(`Failed to register icon ${iconName}:`, error)
    return false
  }
}

/**
 * Gets the total count of configured icons
 * @returns {number} Total icon count
 */
export const getTotalIconCount = () => {
  return Object.values(ICON_STYLES).reduce(
    (total, icons) => total + icons.length,
    0,
  )
}

/**
 * Gets icon count by style
 * @returns {Object} Icon counts by style
 */
export const getIconCountsByStyle = () => {
  const counts = {}
  for (const [style, icons] of Object.entries(ICON_STYLES)) {
    counts[style] = icons.length
  }
  return counts
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use initializeIconLibrarySync instead
 */
export const initializeIconLibrary = initializeIconLibrarySync
